/**
 * @file AdminAuditLog.jsx — /cx/auditoria
 */
import React, { useState, useEffect, useCallback } from "react";
import { adminService } from "../services/admin";
import { ADMIN_STRINGS } from "../constants/strings";
import {
  Card,
  Input,
  Select,
  SelectItem,
  Spinner,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "../ui";
import "./AdminGeneric.css";

const ACTIONS = Object.keys(ADMIN_STRINGS.AUDIT_ACTIONS);

export default function AdminAuditLog() {
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {};
      if (actionFilter) params.action = actionFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await adminService.getAuditLog(params);
      setEntries(res.data || []);
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
    }
  }, [actionFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="ag-page">
      <header className="ag-head">
        <span className="ag-eyebrow">Admin · Auditoría</span>
        <h1 className="ag-title">{ADMIN_STRINGS.AUDIT_TITLE}</h1>
      </header>

      <div className="ag-filters">
        <Select
          label={ADMIN_STRINGS.AUDIT_FILTER_ACTION}
          value={actionFilter}
          onValueChange={(v) => setActionFilter(v === "__all__" ? "" : v)}
          placeholder={ADMIN_STRINGS.AUDIT_ALL_ACTIONS}
        >
          <SelectItem value="__all__">{ADMIN_STRINGS.AUDIT_ALL_ACTIONS}</SelectItem>
          {ACTIONS.map((a) => (
            <SelectItem key={a} value={a}>
              {ADMIN_STRINGS.AUDIT_ACTIONS[a]}
            </SelectItem>
          ))}
        </Select>
        <Input type="date" label="Desde" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Input type="date" label="Hasta" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      </div>

      {isLoading ? (
        <div className="ag-loading">
          <Spinner size={28} />
        </div>
      ) : entries.length === 0 ? (
        <Card className="ag-empty">
          <p>{ADMIN_STRINGS.AUDIT_EMPTY}</p>
        </Card>
      ) : (
        <Card padded={false} className="ag-table-wrap">
          <Table>
            <THead>
              <TR>
                <TH>Fecha</TH>
                <TH>Admin</TH>
                <TH>Acción</TH>
                <TH>Tipo</TH>
                <TH>ID</TH>
                <TH>Detalles</TH>
              </TR>
            </THead>
            <TBody>
              {entries.map((e) => (
                <TR key={e.id}>
                  <TD>
                    <span className="ag-date">
                      {new Date(e.createdAt).toLocaleString("es-MX", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </span>
                  </TD>
                  <TD>#{e.userId}</TD>
                  <TD>{ADMIN_STRINGS.AUDIT_ACTIONS[e.action] || e.action}</TD>
                  <TD>{e.targetType || "—"}</TD>
                  <TD>{e.targetId || "—"}</TD>
                  <TD>
                    <span className="ag-truncate">
                      {e.metadata ? JSON.stringify(e.metadata) : "—"}
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
